import React, { useState, useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  Divider,
  Tooltip,
  CircularProgress,
  Grid,
} from "@mui/material";
import {
  CheckCircleOutline,
  ArrowForward,
  Business,
  AutoAwesome,
  OndemandVideo,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { WhoWeAreImgpowerd } from "../../Common/CommonImages";
import { BASE_URL, handleDynamicRequest } from "../../Common/CommonExport";
import { useDispatch } from "react-redux";
import { setHasPromoVideo } from "../../store/Test/action";
import AssessmentHeader from "../../Components/AssessmentHeader";

function AboutCompany() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [videoConfig, setVideoConfig] = useState(null);
  const [clientName, setClientName] = useState("");

  // Extract the unique ID
  const linkHref = window.location.href;
  const parts = linkHref.split("-");
  const uniqueId = parts[parts.length - 1];

  const [isVideoFinished, setIsVideoFinished] = useState(() => {
    return sessionStorage.getItem(`promoWatched_${uniqueId}`) === "true";
  });

  const handleProceed = () => {
    // Navigating to the summary page keeping the current url path
    const linkPath = window.location.pathname;
    navigate(`/summary${linkPath}`);
  };

  useEffect(() => {
    if (!uniqueId) return;

    // 1. Fetch the Assessment Details
    handleDynamicRequest(
      "GET",
      `${BASE_URL}/public/assessment/${uniqueId}`,
      {},
      (error) => {
        console.error("Failed to fetch assessment", error);
        dispatch(setHasPromoVideo(false));
        handleProceed(); // Skip if fails
      },
      (data) => {
        if (!data || !data.data) {
          dispatch(setHasPromoVideo(false));
          return handleProceed();
        }

        // 2. Extract Client ID and Name
        const clientObj = data.data.createdBy?.clientId;
        const clientId = clientObj?._id || clientObj;
        if (!clientId) {
          dispatch(setHasPromoVideo(false));
          return handleProceed();
        }
        if (clientObj?.companyName || clientObj?.clientName) {
          setClientName(clientObj.companyName || clientObj.clientName);
        }

        // 3. Fetch Promotional Video Config
        const promoUrl = `${BASE_URL}/public/promo-video-config/effective?clientId=${clientId}&module=assessment`;

        handleDynamicRequest(
          "GET",
          promoUrl,
          {},
          (error) => {
            console.error("Failed to fetch promo video", error);
            dispatch(setHasPromoVideo(false));
            handleProceed(); // Skip if fails
          },
          (promoData) => {
            setLoading(false);
            if (promoData?.success && promoData?.data?.show) {
              // Video is configured and active!
              dispatch(setHasPromoVideo(true));
              setVideoConfig(promoData.data);
            } else {
              // No video to show, skip automatically
              dispatch(setHasPromoVideo(false));
              handleProceed();
            }
          },
        );
      },
    );
  }, [uniqueId, dispatch, navigate]);

  // Listen for YouTube/Vimeo video finish events via postMessage
  useEffect(() => {
    const handleMessage = (event) => {
      // Detect YouTube video end (playerState 0)
      if (
        event.origin === "https://www.youtube.com" &&
        typeof event.data === "string"
      ) {
        try {
          const data = JSON.parse(event.data);
          if (
            data.event === "infoDelivery" &&
            data.info &&
            data.info.playerState === 0
          ) {
            setIsVideoFinished(true);
          }
        } catch (e) {}
      }

      // Detect Vimeo video end
      if (
        event.origin === "https://player.vimeo.com" &&
        typeof event.data === "string"
      ) {
        try {
          const data = JSON.parse(event.data);
          if (data.event === "finish") {
            setIsVideoFinished(true);
          }
        } catch (e) {}
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Update sessionStorage whenever isVideoFinished becomes true
  useEffect(() => {
    if (isVideoFinished && uniqueId) {
      sessionStorage.setItem(`promoWatched_${uniqueId}`, "true");
    }
  }, [isVideoFinished, uniqueId]);

  // Helper to safely format YouTube/Vimeo embeds
  const getEmbedUrl = (url) => {
    if (!url) return null;
    const ytMatch = url.match(
      /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/,
    );
    if (ytMatch && ytMatch[1]) {
      return `https://www.youtube.com/embed/${ytMatch[1]}?enablejsapi=1`;
    }
    const vimeoMatch = url.match(
      /(?:vimeo\.com\/)(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]+\/)?videos\/|album\/(?:\d+\/)?video\/|video\/|)(\d+)(?:[a-zA-Z0-9_\-]+)?/i,
    );
    if (vimeoMatch && vimeoMatch[1]) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}?api=1`;
    }
    return url;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: "#f5f7fa",
        }}
      >
        <CircularProgress sx={{ color: "#FF6812" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f5f7fa",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <AssessmentHeader activeStep="About Company" />

      <Box
        sx={{
          flex: 1,
          py: { base: 4, sm: 2 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: { base: "95%", sm: "90%", md: "95%" },
            maxWidth: 1200,
            mx: "auto",
          }}
        >
          <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: "16px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
              p: { base: 1, sm: 2 },
              position: "relative",
            }}
          >
            {/* Header section */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 2,
              }}
            >
              {/* <Box sx={{ bgcolor: "#e0f7fa", color: "#00b4d8", px: 1, py: 0.5, borderRadius: "20px", display: "inline-flex", alignItems: "center", mb: 1 }}>
                <Business sx={{ fontSize: 14, mr: 0.5 }} />
                <Typography sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: 0.5 }}>COMPANY PROFILE</Typography>
              </Box> */}
              <Typography
                sx={{
                  fontSize: { base: "24px", sm: "32px" },
                  fontWeight: 800,
                  color: "#1a1a2e",
                }}
              >
                Welcome to{" "}
                <span
                  style={{
                    background: "linear-gradient(135deg, #10b981, #FF6812)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {clientName || "HireCorrecto"}
                </span>
              </Typography>
            </Box>

            {/* Content Grid */}
            <Grid container spacing={2}>
              {/* Left Column */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    border: "1px solid #eaeaea",
                    borderRadius: "12px",
                    p: { base: 1, sm: 2 },
                    height: { base: "auto", md: "420px" },
                    boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                    overflowY: "auto",
                    "&::-webkit-scrollbar": { width: "6px" },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#ccc",
                      borderRadius: "3px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 700,
                      color: "#333",
                      mb: 0.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: "#e6f8f1",
                        p: 0.75,
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Business sx={{ fontSize: 22, color: "#10b981" }} />
                    </Box>
                    About Company
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#666",
                      lineHeight: 1.6,
                      mb: 4,
                    }}
                  >
                    {videoConfig?.video?.description
                      ? videoConfig.video.description
                          .split("\n")
                          .map((line, idx) => (
                            <span key={idx}>
                              {line}
                              <br />
                            </span>
                          ))
                      : "HireCorrecto is a state-of-the-art talent assessment platform powered by advanced intelligence. We design interactive and intelligent screening technologies to make the recruitment workflow seamless, efficient, and transparent. Our system evaluates both technical competence and behavioral traits to match top candidates with their dream roles."}
                  </Typography>
                </Box>
              </Grid>

              {/* Right Column */}
              <Grid item base={12} md={6}>
                <Box
                  sx={{
                    border: "1px solid #eaeaea",
                    borderRadius: "12px",
                    p: { base: 1, sm: 2 },
                    height: { base: "auto", md: "420px" },
                    boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                    overflowY: "auto",
                    "&::-webkit-scrollbar": { width: "6px" },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#ccc",
                      borderRadius: "3px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 700,
                      color: "#777",
                      letterSpacing: 0.5,
                      mb: 1.5,
                      textTransform: "uppercase",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: "#fff0e6",
                        p: 0.75,
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <OndemandVideo sx={{ fontSize: 22, color: "#FF6812" }} />
                    </Box>
                    Introduction Video
                  </Typography>

                  <Box
                    sx={{
                      width: "100%",
                      aspectRatio: "16/9",
                      bgcolor: "#000",
                      borderRadius: "12px",
                      overflow: "hidden",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                      position: "relative",
                      mb: 2,
                    }}
                  >
                    {videoConfig?.video?.url ? (
                      videoConfig.video.source === "external" ? (
                        <iframe
                          src={getEmbedUrl(videoConfig.video.url)}
                          title="Promotional Video"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          style={{
                            display: "block",
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            border: "none",
                          }}
                          onLoad={(e) => {
                            try {
                              const src = e.target.src;
                              if (src.includes("vimeo.com")) {
                                e.target.contentWindow.postMessage(
                                  JSON.stringify({
                                    method: "addEventListener",
                                    value: "finish",
                                  }),
                                  "*",
                                );
                              } else if (src.includes("youtube.com")) {
                                e.target.contentWindow.postMessage(
                                  JSON.stringify({ event: "listening" }),
                                  "*",
                                );
                              }
                            } catch (err) {}
                          }}
                        ></iframe>
                      ) : (
                        <video
                          controls
                          controlsList="nodownload"
                          onEnded={() => {
                            setIsVideoFinished(true);
                            sessionStorage.setItem(
                              `promoWatched_${uniqueId}`,
                              "true",
                            );
                          }}
                          style={{
                            display: "block",
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        >
                          <source
                            src={videoConfig.video.url}
                            type="video/mp4"
                          />
                          Your browser does not support the video tag.
                        </video>
                      )
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          color: "#fff",
                        }}
                      >
                        <Typography sx={{ fontWeight: 600, fontSize: "16px" }}>
                          Video Unavailable
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Typography
                    sx={{ fontSize: "12px", color: "#888", textAlign: "left" }}
                  >
                    Watch a quick introduction to learn more about HireCorrecto
                    and our work culture.
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Action Row */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 2,
                pt: 2,
                borderTop: "1px solid #f0f0f0",
              }}
            >
              <Tooltip
                title={
                  videoConfig && !videoConfig.allowSkip && !isVideoFinished
                    ? "Please watch the video to proceed"
                    : ""
                }
                placement="top"
              >
                <span>
                  <Button
                    onClick={handleProceed}
                    disabled={
                      videoConfig && !videoConfig.allowSkip && !isVideoFinished
                    }
                    endIcon={<ArrowForward />}
                    sx={{
                      background:
                        videoConfig &&
                        !videoConfig.allowSkip &&
                        !isVideoFinished
                          ? "#e0e0e0"
                          : "#10b981",
                      color:
                        videoConfig &&
                        !videoConfig.allowSkip &&
                        !isVideoFinished
                          ? "#9e9e9e"
                          : "#fff",
                      fontWeight: 600,
                      fontSize: "14px",
                      borderRadius: "8px",
                      textTransform: "none",
                      px: 3,
                      boxShadow:
                        videoConfig &&
                        !videoConfig.allowSkip &&
                        !isVideoFinished
                          ? "none"
                          : "0 4px 12px rgba(16, 185, 129, 0.3)",
                      "&:hover": {
                        background:
                          videoConfig &&
                          !videoConfig.allowSkip &&
                          !isVideoFinished
                            ? "#e0e0e0"
                            : "#059669",
                      },
                    }}
                  >
                    Proceed to Summary
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ mt: 2, mb: 2, display: "flex", justifyContent: "center" }}>
            <img
              src={WhoWeAreImgpowerd}
              alt="Powered by HireCorrecto"
              style={{ height: 28, width: 200 }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default AboutCompany;
